import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { CustomModelValue, DataQuery, PageInfo } from "@models/ISchema";
import { CustomModelValueOption } from "@services/types/custom-models/customModelsTypes";
import { customModelValuesQuery } from "./graphql/customModelValues.query";

/**
 * Page size used by `useGetAllCustomModelValues` to pull a whole custom model
 * in a single request.
 *
 * It is deliberately a single page and not a sweep over every page: the only
 * consumer is the campus selector, whose lists sit around 40-50 entries, so
 * 200 leaves a wide margin without any looping logic. A model larger than this
 * — or a server-side cap below it — would be truncated silently, so raise this
 * value (or move to a paged sweep) before pointing the hook at a bigger model.
 *
 * Unrelated to the paginated `useGetCustomModelValues` below, which keeps its
 * own page size.
 */
const ALL_CUSTOM_MODEL_VALUES_PAGE_SIZE = 200;

interface useGetCustomModelValuesParams {
  scenario: string;
  origin: string;
  customModelCode: string;
  getOptionLabel?: (option: CustomModelValue) => string;
  ignoreAccessFilters?: boolean;
  /**
   * Key to reset the search term and page
   */
  key?: string;
}

const useGetCustomModelValues = ({
  scenario,
  origin,
  customModelCode,
  getOptionLabel,
  ignoreAccessFilters,
  key,
}: useGetCustomModelValuesParams) => {
  const client = useApolloClient();
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [customModelValuesPage, setCustomModelValuesPage] = useState(0);

  useEffect(() => {
    setLocalSearchTerm("");
    setCustomModelValuesPage(0);
  }, [key]);

  const getCustomModelValues = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;

      try {
        const variables = {
          query: customModelValuesQuery,
          variables: {
            scenarioId: scenario,
            originId: origin,
            filter: {
              fields: customModelCode
                ? {
                    customModel: {
                      code: {
                        eq: customModelCode,
                      },
                    },
                  }
                : undefined,
              ...(ignoreAccessFilters !== undefined && { ignoreAccessFilters }),
              orderBy: {
                field: "code",
                direction: "ASC",
              },
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: DataQuery = data?.data;
        const pageInfo: PageInfo = dataQuery?.customModelValues?.pageInfo;

        const options = [];

        if (dataQuery?.customModelValues?.items?.length > 0) {
          dataQuery?.customModelValues?.items.forEach((customModelValue: CustomModelValue) => {
            options.push({
              label: getOptionLabel ? getOptionLabel(customModelValue) : customModelValue?.code,
              value: customModelValue?.id,
              self: customModelValue,
            });
          });
        }

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [client, customModelCode, origin, scenario, ignoreAccessFilters],
  );

  const loadCustomModelValues = async (searchTerm: string) => {
    const newSearchPage = localSearchTerm === searchTerm ? customModelValuesPage + 1 : 1;

    setLocalSearchTerm(searchTerm);
    setCustomModelValuesPage(newSearchPage);

    const { pageInfo, options } = await getCustomModelValues(searchTerm, newSearchPage);

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page: customModelValuesPage },
    };
  };

  return {
    loadCustomModelValues,
  };
};

interface useGetAllCustomModelValuesParams {
  scenario: string;
  origin?: string;
  customModelCode: string;
  getOptionLabel?: (option: CustomModelValue) => string;
  ignoreAccessFilters?: boolean;
}

/**
 * Loads every value of a custom model in one request, for selectors that need
 * the full list in memory (client-side filtering, bulk selection) instead of
 * paging through it.
 *
 * Use it only for models known to be small — see
 * `ALL_CUSTOM_MODEL_VALUES_PAGE_SIZE`. For anything unbounded, use the
 * paginated `useGetCustomModelValues`.
 */
export const useGetAllCustomModelValues = ({
  scenario,
  origin,
  customModelCode,
  getOptionLabel,
  ignoreAccessFilters,
}: useGetAllCustomModelValuesParams) => {
  const client = useApolloClient();
  const [options, setOptions] = useState<CustomModelValueOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Held in a ref so a caller passing an inline formatter does not retrigger
  // the request on every render.
  const getOptionLabelRef = useRef(getOptionLabel);
  getOptionLabelRef.current = getOptionLabel;

  useEffect(() => {
    let isStale = false;

    const getAllCustomModelValues = async () => {
      setLoading(true);

      try {
        const { data } = await client.query({
          query: customModelValuesQuery,
          variables: {
            scenarioId: scenario,
            originId: origin,
            filter: {
              fields: customModelCode
                ? {
                    customModel: {
                      code: {
                        eq: customModelCode,
                      },
                    },
                  }
                : undefined,
              ...(ignoreAccessFilters !== undefined && { ignoreAccessFilters }),
              orderBy: {
                field: "code",
                direction: "ASC",
              },
              pagination: {
                page: 1,
                size: ALL_CUSTOM_MODEL_VALUES_PAGE_SIZE,
                searchTerm: "",
              },
            },
          },
        });

        if (isStale) return;

        const dataQuery: DataQuery = data?.data;
        const items = dataQuery?.customModelValues?.items ?? [];

        setOptions(
          items.map((customModelValue: CustomModelValue) => ({
            label: getOptionLabelRef.current
              ? getOptionLabelRef.current(customModelValue)
              : customModelValue?.code,
            value: customModelValue?.id,
            self: customModelValue,
          })),
        );
      } catch (error) {
        console.error(error);

        if (!isStale) setOptions([]);
      } finally {
        if (!isStale) setLoading(false);
      }
    };

    if (scenario && customModelCode) getAllCustomModelValues();

    return () => {
      isStale = true;
    };
  }, [client, scenario, origin, customModelCode, ignoreAccessFilters]);

  return {
    options,
    loading,
  };
};

export default useGetCustomModelValues;
